import { MutationTree } from "vuex";
import {
  EnabledAirline,
  IFlightSearch,
} from "@/core/interfaces/IFlightSearch.interface";
import type { IPassenger } from "@/core/interfaces/passenger.interface";
import {
  FlightResponse,
  MultiCityLegStateType,
  SelectedFlightLeg,
  FareOption,
  FlightOption,
  ACTIVE_PROVIDERS_AND_AIRLINES_API_RESPONSE,
  TravelerCount,
  IMultiCityAirports,
  Place,
} from "@/core/types/flight";

import { ROUTE_TYPE } from "@/core/enums/ROUTE_TYPE";
import { CABIN_CLASS } from "@/core/enums/CABIN_CLASS";
import { FinancialProfile } from "@/modules/Auth/types";
import { AmountType } from "@/modules/FlightSearchResult/types";

const mutations: MutationTree<IFlightSearch> = {
  setIsPolling(state, payload: boolean) {
    state.isPolling = payload;
  },
  setFlightsResponse(state, flights: FlightResponse) {
    state.flightsResponse = flights;
  },
  loadingFlights(state, payload: boolean) {
    state.isFlightFetching = payload;
  },
  setLoading(state, payload: boolean) {
    state.isLoading = payload;
  },
  saveFlights(state, flights: FlightResponse) {
    const filteredFlights =
      flights.journey_legs[state.currentLegIndex].flight_options;
    state.isFlightFetching = false;
    state.flightsResponse = flights;
    state.flights = filteredFlights;
    state.selectedFilteredLegsByFareIds.push(filteredFlights);
    state.lastLegIndex = flights.journey_legs.length - 1;
  },
  loadingAirports(state, payload: boolean) {
    state.isAirportFetching = payload;
  },
  saveSelectedAirlines(state, payload: string[] | null) {
    state.selectedAirlines = payload;
  },
  saveAirports(state, airports: Array<Place>) {
    state.isAirportFetching = false;
    state.airports = airports;
  },
  saveOriginAirports(state, airports: Array<Place>) {
    state.originAirports = airports;
  },
  saveSearchFinancialProfiles(
    state,
    finanicalProfiles: Array<FinancialProfile> | null,
  ) {
    state.searchFinancialProfiles = finanicalProfiles;
  },
  saveEnabledAirlines(state, enabledAirlines: Array<EnabledAirline> | null) {
    state.enabledAirlines = enabledAirlines;
  },
  saveCurrencyCodes(state, currencyCodes: string[] | null) {
    state.currencyCodes = currencyCodes;
  },
  saveSelectedCurrencyCode(state, payload: string | null) {
    state.selectedCurrencyCode = payload;
  },
  saveSelectedFinancialProfile(state, payload: string) {
    state.selectedFinancialProfile = payload;
  },
  saveDestinationAirports(state, airports: Array<Place>) {
    state.destinationAirports = airports;
  },
  loadingOriginAirports(state: IFlightSearch, isLoading: boolean) {
    state.loadingOriginAirports = isLoading;
  },
  loadingDestinationAirports(state: IFlightSearch, isLoading: boolean) {
    state.loadingDestinationAirports = isLoading;
  },
  loadingFinancialProfiles(state: IFlightSearch, isLoading: boolean) {
    state.loadingFinancialProfiles = isLoading;
  },
  saveOrigin(state, payload: Place) {
    state.origin = payload;
  },
  saveDestination(state, payload: Place) {
    state.destination = payload;
  },
  saveDepartureDate(state, payload: Date) {
    state.departure_date = payload;
  },
  saveReturnDate(state, payload: Date) {
    state.return_date = payload;
  },
  saveCabinClass(state, payload: CABIN_CLASS) {
    state.cabin_class = payload;
  },
  saveRouteType(state, payload: ROUTE_TYPE) {
    state.route_type = payload;
  },
  setInitializedFlag(state, payload: boolean) {
    state.hasInitializedFromUrl = payload;
  },
  saveAdultCount(state, payload: number) {
    state.adult_count = payload;
  },
  saveChildCount(state, payload: number) {
    state.child_count = payload;
  },
  saveInfantCount(state, payload: number) {
    state.infant_count = payload;
  },
  saveTravelersCount(state, payload: TravelerCount) {
    state.adult_count = payload.adult_count;
    state.child_count = payload.child_count;
    state.infant_count = payload.infant_count;
  },
  setSelectedPassengerIds(state, payload: string[]) {
    state.selectedPassengerIds = payload;
    state.selectedPassengerProfiles = state.selectedPassengerProfiles.filter(
      (passenger) =>
        Boolean(passenger.public_id) &&
        payload.includes(passenger.public_id as string),
    );
  },
  addSelectedPassengerId(state, payload: string) {
    if (!state.selectedPassengerIds.includes(payload)) {
      state.selectedPassengerIds.push(payload);
    }
  },
  removeSelectedPassengerId(state, payload: string) {
    state.selectedPassengerIds = state.selectedPassengerIds.filter(
      (id) => id !== payload,
    );
    state.selectedPassengerProfiles = state.selectedPassengerProfiles.filter(
      (passenger) => passenger.public_id !== payload,
    );
  },
  upsertSelectedPassengerProfiles(state, payload: IPassenger[]) {
    const profilesById = new Map(
      state.selectedPassengerProfiles.map((passenger) => [
        passenger.public_id,
        passenger,
      ]),
    );

    payload.forEach((passenger) => {
      if (!passenger.public_id) return;
      profilesById.set(passenger.public_id, passenger);
    });

    state.selectedPassengerProfiles = Array.from(profilesById.values());
  },
  setFetchingSelectedPassengerProfiles(state, payload: boolean) {
    state.isFetchingSelectedPassengerProfiles = payload;
  },
  setGuestTravelerCounts(state, payload: TravelerCount) {
    state.guestTravelerCounts = payload;
  },
  resetGuestTravelerCounts(state) {
    state.guestTravelerCounts = {
      adult_count: 0,
      child_count: 0,
      infant_count: 0,
    };
  },
  clearTravelerSelection(state) {
    state.selectedPassengerIds = [];
    state.selectedPassengerProfiles = [];
    state.isFetchingSelectedPassengerProfiles = false;
    state.guestTravelerCounts = {
      adult_count: 0,
      child_count: 0,
      infant_count: 0,
    };
    state.adult_count = 1;
    state.child_count = 0;
    state.infant_count = 0;
  },
  saveMulticityLegs(state, payload: MultiCityLegStateType[]) {
    state.multiCityLegs = payload;
  },
  saveServiceFee(state: IFlightSearch, payload: number | null) {
    if (!state.pricing) {
      state.pricing = {
        service_fees: payload,
        pricing_type: AmountType.FIXED,
        apply_on_gross_fare: true,
      };
    } else {
      state.pricing.service_fees = payload;
    }
  },

  savePricingType(state: IFlightSearch, payload: string) {
    if (!state.pricing) {
      state.pricing = {
        service_fees: 0,
        pricing_type: payload,
        apply_on_gross_fare: true,
      };
    } else {
      state.pricing.pricing_type = payload;
    }
  },
  saveApplyOnGrossFare(state: IFlightSearch, payload: boolean) {
    if (!state.pricing) {
      state.pricing = {
        service_fees: 0,
        pricing_type: AmountType.FIXED,
        apply_on_gross_fare: payload,
      };
    } else {
      state.pricing.apply_on_gross_fare = payload;
    }
  },
  addMulticityFlight(state, payload: MultiCityLegStateType) {
    state.multiCityLegs.push(payload);
  },
  deleteMulticityFlight(state, index: number) {
    const _legs = state.multiCityLegs;
    const airports = state.multiCityAirports;
    airports.splice(index, 1);
    _legs.splice(index, 1);
    state.multiCityAirports = airports;
    state.multiCityLegs = _legs;
  },
  updateMulticityDepartureDate(state, payload) {
    const _legs = state.multiCityLegs;
    _legs[payload.index].departure_date = payload.date;

    const updated_legs = _legs.map((x, index) => {
      if (index > payload.index && x.departure_date < payload.date) {
        return {
          ...x,
          departure_date: payload.date,
        };
      }

      return x;
    });

    state.multiCityLegs = updated_legs;
  },
  updateMulticityOrigin(state, payload) {
    state.multiCityLegs[payload.index].origin = payload.origin;
  },
  updateMulticityDestination(state, payload) {
    const {
      index,
      destination: selectedDestination,
      autoSelectNextOrigin = true,
    } = payload;

    const currentLeg = state.multiCityLegs[index];
    const previousDestination = currentLeg?.destination;
    currentLeg.destination = selectedDestination;

    if (!autoSelectNextOrigin || !selectedDestination) {
      return;
    }

    const nextLeg = state.multiCityLegs[index + 1];
    const nextLegAirportOptions = state.multiCityAirports[index + 1];
    if (!nextLeg || !nextLegAirportOptions) {
      return;
    }

    const nextOriginOptions = nextLegAirportOptions.origin;
    const isDestinationInNextOriginOptions = nextOriginOptions.some(
      (airport) => airport.iata_code === selectedDestination.iata_code,
    );
    if (!isDestinationInNextOriginOptions) {
      nextOriginOptions.push(selectedDestination);
    }

    const nextOrigin = nextLeg.origin;
    const nextOriginIsEmpty = !nextOrigin;
    const nextOriginStillMatchesPreviousDestination =
      !!previousDestination &&
      !!nextOrigin &&
      nextOrigin.iata_code === previousDestination.iata_code;

    const shouldUpdateNextOrigin =
      nextOriginIsEmpty || nextOriginStillMatchesPreviousDestination;

    if (shouldUpdateNextOrigin) {
      nextLeg.origin = selectedDestination;
    }
  },
  saveFilters(state, payload) {
    state.filters = payload;
  },
  saveSelectedFlightFilters(state, payload) {
    state.selectedFlightFilters = payload;
  },
  saveAmountType(state, payload) {
    state.amountType = payload;
  },
  saveFareType(state, payload) {
    state.fareType = payload;
  },
  saveFareCalculatorAmount(state, payload) {
    state.fareCalculatorAmount = payload;
  },
  selectFlightLeg(state, payload: SelectedFlightLeg) {
    state.selectedFlightLegs.push(payload);
  },
  saveCurrentLegIndex(state, payload: number) {
    state.currentLegIndex = payload;
  },
  clearSelectedFlightLegs(state) {
    state.selectedFlightLegs = [];
    state.selectedFares = [];
    state.selectedFilteredLegsByFareIds = [];
    state.currentLegIndex = 0;
    state.flights = [];
  },
  removeSelectedFlightLeg(state, index: number) {
    state.currentLegIndex = index === 0 ? 0 : index - 1;
    state.selectedFlightLegs.splice(index);
    state.selectedFares.splice(index);
    if (index === 0) {
      state.selectedFilteredLegsByFareIds.splice(index + 1);
    } else {
      state.selectedFilteredLegsByFareIds.splice(index);
    }
  },
  selectFlightFare(state, payload: FareOption) {
    state.selectedFares.push(payload);
  },
  saveFilteredLegsByFareIds(state, payload: FlightOption[]) {
    state.selectedFilteredLegsByFareIds.push(payload);
  },
  addActiveAirlinesAndProviders(
    state,
    payload: ACTIVE_PROVIDERS_AND_AIRLINES_API_RESPONSE,
  ) {
    state.sabreAirlines = payload.active_sabre_airlines;
    state.galileoAirline = payload.active_galileo_airlines;
    state.flightProviders = payload.active_flight_providers;
  },
  resetActiveAirlinesAndProviders(state) {
    state.sabreAirlines = [];
    state.flightProviders = [];
  },
  resetFlightSearchProperties(state) {
    state.origin = null;
    state.destination = null;
    state.departure_date = new Date();
    state.return_date = null;
    state.route_type = ROUTE_TYPE.ONEWAY;
    state.adult_count = 1;
    state.child_count = 0;
    state.infant_count = 0;
    state.selectedPassengerIds = [];
    state.selectedPassengerProfiles = [];
    state.isFetchingSelectedPassengerProfiles = false;
    state.guestTravelerCounts = {
      adult_count: 0,
      child_count: 0,
      infant_count: 0,
    };
    state.cabin_class = CABIN_CLASS.ECONOMY;
  },
  setMulticityAirportInputLoading(
    state,
    payload: { type: "origin" | "destination"; index: number },
  ) {
    state.currentMultiCityAirportInputIndexLoading = payload.index;
    state.currentMultiCityAirportInputTypeLoading = payload.type;
  },
  saveBulkMulticityAirports(state, payload: IMultiCityAirports[]) {
    state.multiCityAirports = payload;
  },
  setMulticityAirports(
    state,
    payload: {
      airports: Place[];
      type: "origin" | "destination";
      index: number;
    },
  ) {
    while (state.multiCityAirports.length <= payload.index) {
      state.multiCityAirports.push({
        origin: [],
        destination: [],
      });
    }
    state.multiCityAirports[payload.index][payload.type] = payload.airports;
  },
  addNewMulticityAirportsRow(state) {
    const newRow = {
      origin: state.airports,
      destination: state.airports,
    };
    state.multiCityAirports.push(newRow);
  },
};

export default mutations;
